import {
  Configuration,
  ErrorErrorID,
  NotificationApi,
  ProductApi,
  ResponseError,
  SiteApi,
  type Middleware
} from '@linktivity/ars-neo';
import {
  AGENT_HEADER,
  AUTHORIZATION_HEADER,
  UNAUTHORIZED
} from '@link/share/constants';

/**
 * ARS API clients, for loaders on the server and for components in the
 * browser alike.
 *
 * Deliberately separate from `src/services`, the SPA's: its middleware pops
 * link-ui toasts, redirects via `window.location` and translates with the
 * SPA's i18n singleton, which pulls every language's translations into the
 * bundle. Here a request does only what a request should — send the agent and,
 * for a visitor, their token — and a failure is thrown as a `ResponseError`
 * for the caller to handle: a loader turns it into a route error (see
 * toRouteError), a component tells the visitor in its own words.
 *
 * Same generated SDK and the same types as the SPA, so request/response shapes
 * cannot drift between the two.
 *
 * NOTE: `@linktivity/ars-neo` used to need `ssr.noExternal` — its dist was ESM
 * syntax without `"type": "module"` and with extensionless relative imports
 * (`export * from './runtime'`), which Node's native ESM loader rejects with
 * ERR_MODULE_NOT_FOUND. Fixed upstream in 0.3.9, so the workaround is gone and
 * the package is externalised like any other. Kept as a note because the same
 * packaging flaw is easy to reintroduce in a regenerated SDK, and it surfaces
 * only under a real server start — a browser-only build resolves it fine.
 */

// Every request needs the agent or the backend answers 404 "agent id not found
// in context".
const AGENT_ID = 'triplabo';

/** Where a visitor's requests get their token, and a new one when it expires. */
export interface ArsCredentials {
  getAccessToken: () => string | undefined;
  /** A fresh access token, or `undefined` when the session cannot be renewed. */
  refreshAccessToken: () => Promise<string | undefined>;
  /** The API refused the token for good: invalid, or still refused once renewed. */
  onRejected: () => void;
}

/**
 * Sends the visitor's token. On an expired one it refreshes the token and
 * retries the request once; the retry goes back through `pre`, so it carries
 * the new token. A token refused any other way, or refused again after the
 * refresh, ends the session. Either way the failure is left for the SDK to
 * throw.
 */
function credentialsMiddleware(credentials: ArsCredentials): Middleware {
  const retried = new WeakSet<RequestInit>();
  return {
    async pre(context) {
      const headers = new Headers(context.init.headers);
      const accessToken = credentials.getAccessToken();
      if (accessToken) {
        headers.set(AUTHORIZATION_HEADER, accessToken);
      } else {
        headers.delete(AUTHORIZATION_HEADER);
      }
      // Mutated in place rather than returned: `post` recognises the retry by
      // this same init object.
      context.init.headers = headers;
    },
    async post(context) {
      if (context.response.status !== UNAUTHORIZED) return;
      const data = await context.response
        .clone()
        .json()
        .catch(() => null);
      const expired = data?.error_id === ErrorErrorID.ExpiredToken;
      if (!expired || retried.has(context.init)) {
        credentials.onRejected();
        return;
      }
      // An unrenewable session is ended by the refresh itself; a refresh that
      // never reached the API keeps the tokens for a later attempt.
      if (!(await credentials.refreshAccessToken())) return;
      const init = { ...context.init };
      retried.add(init);
      return context.fetch(context.url, init);
    }
  };
}

/**
 * An SDK configuration. Without credentials the requests are anonymous, which
 * is all a server render ever sends: the HTML is the same for every visitor.
 */
export function createArsConfig(credentials?: ArsCredentials) {
  const basePath = import.meta.env.VITE_APP_ARS_API as string | undefined;
  if (!basePath) {
    throw new Error('VITE_APP_ARS_API is not set; the ARS API is unreachable.');
  }
  return new Configuration({
    basePath,
    headers: {
      'Content-Type': 'application/json',
      [AGENT_HEADER]: AGENT_ID
    },
    middleware: credentials ? [credentialsMiddleware(credentials)] : []
  });
}

const anonymous = createArsConfig();

export const siteApi = new SiteApi(anonymous);
export const productApi = new ProductApi(anonymous);
export const notificationApi = new NotificationApi(anonymous);

/**
 * Convert an SDK failure into a `Response` the router can render.
 *
 * Loaders are expected to throw a `Response`; react-router then routes it to
 * the nearest `ErrorBoundary` with the status intact, instead of surfacing a
 * raw SDK error object.
 */
export async function toRouteError(error: unknown): Promise<Response> {
  if (error instanceof ResponseError) {
    return new Response(error.response.statusText || 'Upstream API error', {
      status: error.response.status
    });
  }
  return new Response('Upstream API request failed', { status: 502 });
}
